import { getAuth } from 'firebase/auth';
import { addDoc, collection, doc, getFirestore, setDoc, Timestamp } from 'firebase/firestore';
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiDollarSign, FiMusic, FiRefreshCw, FiUploadCloud } from 'react-icons/fi';
import { app } from '../firebase';
import { listWithdrawalRequests, markWithdrawalPaid } from '../services/payments';
import './AdminCatalog.css';

const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

const MAX_AUDIO_SIZE = 50 * 1024 * 1024;
const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'wav', 'ogg', 'flac', 'aac'];

const PRODUCERS = [
    {
        id: 'alias_motor_city_signal',
        name: 'Motor City Signal',
        bio: 'Detroit bounce, tight 808s, and fast-pocket drums built for high-energy verses.',
        lane: 'Detroit / Trap',
        palette: ['#111827', '#f97316', '#fde68a'],
        tracks: ['follow my drip', 'crash out'],
    },
    {
        id: 'alias_obsidian_north',
        name: 'Obsidian North',
        bio: 'Dark trap textures with cold melodies, heavy movement, and space for sharp hooks.',
        lane: 'Dark Trap',
        palette: ['#020617', '#c98516', '#f2bd62'],
        tracks: ['abyss', 'pursuit', 'just business'],
    },
    {
        id: 'alias_vanta_loop',
        name: 'Vanta Loop',
        bio: 'Polished trap loops with clean bounce, bright pockets, and modern radio-ready structure.',
        lane: 'Modern Trap',
        palette: ['#18181b', '#22c55e', '#f8fafc'],
        tracks: ['timeless', 'modern classic', 'sweet'],
    },
    {
        id: 'alias_wraith_keys',
        name: 'Wraith Keys',
        bio: 'Melodic pain beats, soft keys, and late-night chords for emotional records.',
        lane: 'Melodic Trap',
        palette: ['#312e81', '#ec4899', '#fdf2f8'],
        tracks: ['sick love', 'promises'],
    },
    {
        id: 'alias_pluto_archive',
        name: 'Pluto Archive',
        bio: 'Cloudy melodies, washed synths, and loose drums inspired by underground playlist energy.',
        lane: 'Cloud Trap',
        palette: ['#0f172a', '#06b6d4', '#c4b5fd'],
        tracks: ['project stories', 'superman'],
    },
    {
        id: 'alias_noir_bounce',
        name: 'Noir Bounce',
        bio: 'Smooth bounce, minimal hooks, and glossy trap pockets for artists who need space.',
        lane: 'Bounce Trap',
        palette: ['#171717', '#eab308', '#f5f5f5'],
        tracks: ['on you'],
    },
];

const TRACK_OVERRIDES = {
    'follow my drip': { title: 'Follow My Drip', genre: 'Detroit', bpm: 160, price: 18 },
    'crash out': { title: 'Crash Out', genre: 'Trap', bpm: 150, price: 22 },
    abyss: { title: 'Abyss', genre: 'Trap', bpm: 146, price: 20 },
    pursuit: { title: 'Pursuit', genre: 'Trap', bpm: 144, price: 19 },
    'just business': { title: 'Just Business', genre: 'Trap', bpm: 142, price: 21 },
    timeless: { title: 'Timeless', genre: 'Trap', bpm: 140, price: 20 },
    'modern classic': { title: 'Modern Classic', genre: 'Trap', bpm: 142, price: 24 },
    sweet: { title: 'Sweet', genre: 'Trap', bpm: 138, price: 16 },
    'sick love': { title: 'Sick Love', genre: 'Melodic', bpm: 140, price: 17 },
    promises: { title: 'Promises', genre: 'Melodic', bpm: 142, price: 18 },
    'project stories': { title: 'Project Stories', genre: 'Cloud Trap', bpm: 124, price: 25 },
    superman: { title: 'Superman', genre: 'Cloud Trap', bpm: 124, price: 23 },
    'on you': { title: 'On You', genre: 'Trap', bpm: 140, price: 15 },
};

const normalize = (value) => value
    .toLowerCase()
    .replace(/\.[^.]+$/, '')
    .replace(/\(128k\)|\(1080p\)|\[free\]|dariusjames beatz|type beat|trap|2024|x|“|”|-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getExtension = (fileName) => fileName.split('.').pop()?.toLowerCase() || '';

const getAudioContentType = (file) => {
    const extension = getExtension(file.name);
    const fallbackTypes = {
        mp3: 'audio/mpeg',
        m4a: 'audio/mp4',
        wav: 'audio/wav',
        ogg: 'audio/ogg',
        flac: 'audio/flac',
        aac: 'audio/aac',
    };

    return file.type || fallbackTypes[extension] || 'audio/mpeg';
};

const safeFileName = (fileName) => fileName.replace(/[^a-zA-Z0-9._-]/g, '_');

const findTrackKey = (fileName) => {
    const cleaned = normalize(fileName);
    return Object.keys(TRACK_OVERRIDES).find((key) => cleaned.includes(key));
};

const findProducer = (trackKey) => PRODUCERS.find((producer) => producer.tracks.includes(trackKey));

const titleCase = (value) => value
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatMoney = (amount) => {
    const numericAmount = Number(amount || 0);
    return Number.isFinite(numericAmount) ? numericAmount.toFixed(2) : '0.00';
};

const formatDate = (value) => {
    if (!value) return 'Unknown';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleDateString();
};

const generatedCover = ({ title, producer }) => {
    const [background, accent, foreground] = producer.palette;
    const initials = producer.name
        .split(' ')
        .map((word) => word[0])
        .join('');
    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="900" height="900" viewBox="0 0 900 900">
            <rect width="900" height="900" fill="${background}"/>
            <circle cx="720" cy="160" r="180" fill="${accent}" opacity="0.88"/>
            <rect x="72" y="72" width="756" height="756" rx="38" fill="none" stroke="${foreground}" stroke-width="4" opacity="0.62"/>
            <text x="78" y="156" fill="${foreground}" font-family="Inter, Arial, sans-serif" font-size="42" font-weight="800">${producer.name}</text>
            <text x="78" y="214" fill="${accent}" font-family="Inter, Arial, sans-serif" font-size="26" font-weight="700">${producer.lane}</text>
            <text x="78" y="676" fill="${foreground}" font-family="Inter, Arial, sans-serif" font-size="74" font-weight="900">${title}</text>
            <text x="78" y="752" fill="${foreground}" opacity="0.68" font-family="Inter, Arial, sans-serif" font-size="30" font-weight="700">Tune Beta Catalog</text>
            <text x="650" y="806" fill="${background}" font-family="Inter, Arial, sans-serif" font-size="84" font-weight="900">${initials}</text>
        </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const generatedAvatar = (producer) => {
    const [background, accent, foreground] = producer.palette;
    const initials = producer.name
        .split(' ')
        .map((word) => word[0])
        .join('');
    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="320" height="320" viewBox="0 0 320 320">
            <rect width="320" height="320" rx="48" fill="${background}"/>
            <circle cx="238" cy="80" r="74" fill="${accent}"/>
            <text x="42" y="190" fill="${foreground}" font-family="Inter, Arial, sans-serif" font-size="82" font-weight="900">${initials}</text>
        </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const AdminCatalog = () => {
    const [files, setFiles] = useState([]);
    const [isSeeding, setIsSeeding] = useState(false);
    const [progress, setProgress] = useState([]);
    const [error, setError] = useState('');
    const [withdrawals, setWithdrawals] = useState([]);
    const [withdrawalsLoading, setWithdrawalsLoading] = useState(false);
    const [withdrawalError, setWithdrawalError] = useState('');
    const [payingWithdrawalId, setPayingWithdrawalId] = useState('');

    const plannedFiles = useMemo(() => {
        const seen = new Set();
        return files.map((file) => {
            const extension = getExtension(file.name);
            const trackKey = findTrackKey(file.name);
            const producer = trackKey ? findProducer(trackKey) : null;
            const metadata = trackKey ? TRACK_OVERRIDES[trackKey] : null;
            const duplicate = trackKey ? seen.has(trackKey) : false;
            if (trackKey) seen.add(trackKey);
            const isAudio = AUDIO_EXTENSIONS.includes(extension);
            const tooLarge = file.size > MAX_AUDIO_SIZE;

            return {
                file,
                extension,
                trackKey,
                producer,
                metadata,
                duplicate,
                isAudio,
                skipReason: !isAudio
                    ? 'Video or unsupported file'
                    : tooLarge
                        ? 'Over 50MB'
                    : duplicate
                        ? 'Duplicate title'
                        : !trackKey || !producer || !metadata
                            ? 'No mapping'
                            : '',
            };
        });
    }, [files]);

    const readyFiles = plannedFiles.filter((item) => !item.skipReason);

    const fetchWithdrawals = useCallback(async () => {
        setWithdrawalsLoading(true);
        setWithdrawalError('');
        try {
            const result = await listWithdrawalRequests({ status: 'pending', limit: 50 });
            setWithdrawals(result.requests || []);
        } catch (err) {
            setWithdrawalError(err.message || 'Could not load withdrawal requests. Make sure this account is an admin.');
        } finally {
            setWithdrawalsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchWithdrawals();
    }, [fetchWithdrawals]);

    const handleMarkPaid = async (withdrawalId) => {
        const note = window.prompt('Optional payout note or transaction reference:', '');
        if (note === null) return;

        setPayingWithdrawalId(withdrawalId);
        setWithdrawalError('');
        try {
            await markWithdrawalPaid({ withdrawalId, note });
            await fetchWithdrawals();
        } catch (err) {
            setWithdrawalError(err.message || 'Could not mark withdrawal paid.');
        } finally {
            setPayingWithdrawalId('');
        }
    };

    const handleFiles = (event) => {
        setError('');
        setProgress([]);
        setFiles(Array.from(event.target.files || []));
    };

    const logProgress = (message, type = 'info') => {
        setProgress((current) => [...current, { message, type, id: `${Date.now()}-${Math.random()}` }]);
    };

    const uploadFile = async ({ file, producer, metadata }) => {
        const user = auth.currentUser;
        const storagePath = `tracks/${user.uid}/catalog_seed/${producer.id}/${Date.now()}_${safeFileName(file.name)}`;
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, file, {
            contentType: getAudioContentType(file),
        });

        await new Promise((resolve, reject) => {
            uploadTask.on('state_changed', null, reject, resolve);
        });

        const fileUrl = await getDownloadURL(uploadTask.snapshot.ref);
        return {
            fileUrl,
            title: metadata.title,
            storagePath,
        };
    };

    const seedCatalog = async () => {
        const user = auth.currentUser;
        if (!user) {
            setError('Sign in first. This tool needs your account to upload files and write seed data.');
            return;
        }

        if (!readyFiles.length) {
            setError('Select mapped MP3/M4A files before seeding.');
            return;
        }

        setIsSeeding(true);
        setError('');
        setProgress([]);

        try {
            logProgress('Creating producer profile docs...');
            await Promise.all(PRODUCERS.map((producer) => setDoc(doc(db, 'profiles', producer.id), {
                profile: {
                    name: producer.name,
                    bio: producer.bio,
                    profilePicture: generatedAvatar(producer),
                    socialLinks: {
                        twitter: '',
                        instagram: '',
                        facebook: '',
                    },
                },
                tracks: [],
                followers: [],
                following: [],
                recentActivity: [`Added to Tune beta catalog on ${new Date().toLocaleDateString()}`],
                accountType: 'producer',
                isDemoAlias: true,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            }, { merge: true })));
            logProgress(`Created ${PRODUCERS.length} producer profiles.`, 'success');

            for (const item of readyFiles) {
                const { file, producer, metadata, trackKey } = item;
                logProgress(`Uploading ${metadata.title}...`);
                const upload = await uploadFile({ file, producer, metadata });
                const trackData = {
                    title: metadata.title,
                    fileUrl: upload.fileUrl,
                    userId: producer.id,
                    artist: producer.name,
                    coverImage: generatedCover({ title: metadata.title, producer }),
                    genre: metadata.genre,
                    genres: [metadata.genre, producer.lane],
                    price: metadata.price,
                    bpm: metadata.bpm,
                    uploadedAt: Timestamp.now(),
                    createdAt: Timestamp.now(),
                    isTopSeller: ['follow my drip', 'abyss', 'modern classic', 'project stories'].includes(trackKey),
                    salesCount: Math.floor(4 + Math.random() * 24),
                    totalRevenue: Number((metadata.price * (3 + Math.random() * 12)).toFixed(2)),
                    views: Math.floor(80 + Math.random() * 700),
                    viewCount: Math.floor(80 + Math.random() * 700),
                };

                await addDoc(collection(db, 'tracks'), trackData);
                logProgress(`Seeded ${metadata.title} by ${producer.name}.`, 'success');
            }

            logProgress('Catalog seed finished. Restore secure rules now with npm run deploy:rules.', 'success');
        } catch (err) {
            console.error('Catalog seed failed:', err);
            setError(err.message || 'Catalog seed failed. Make sure seed rules are deployed.');
            logProgress('Seed failed. Check Firebase rules and console output.', 'error');
        } finally {
            setIsSeeding(false);
        }
    };

    return (
        <main className="admin-catalog-page">
            <section className="admin-catalog-hero">
                <span className="admin-catalog-kicker"><FiMusic /> Beta catalog seeder</span>
                <h1>Upload beats and create alias producer profiles.</h1>
                <p>Select the beat files once. Tune uploads the audio, generates covers, splits tracks across six producer aliases, and writes marketplace-ready Firestore docs.</p>
            </section>

            <section className="admin-catalog-table admin-withdrawals-panel">
                <div className="admin-withdrawals-header">
                    <div>
                        <span className="admin-catalog-kicker"><FiDollarSign /> Payout operations</span>
                        <h2>Pending withdrawal requests</h2>
                    </div>
                    <button type="button" className="admin-refresh-button" onClick={fetchWithdrawals} disabled={withdrawalsLoading}>
                        <FiRefreshCw />
                        {withdrawalsLoading ? 'Loading...' : 'Refresh'}
                    </button>
                </div>

                {withdrawalError && <div className="admin-catalog-error inline">{withdrawalError}</div>}

                {!withdrawalsLoading && withdrawals.length === 0 && (
                    <div className="admin-empty-state">No pending withdrawal requests.</div>
                )}

                {withdrawals.map((withdrawal) => (
                    <article key={withdrawal.id} className="admin-withdrawal-row">
                        <div>
                            <strong>{withdrawal.producerName}</strong>
                            <span>{withdrawal.producerId}</span>
                        </div>
                        <div>
                            <strong>${formatMoney(withdrawal.amount)}</strong>
                            <span>Requested {formatDate(withdrawal.requestedAt)}</span>
                        </div>
                        <div>
                            <strong>{withdrawal.withdrawalMethod.replace('_', ' ')}</strong>
                            <span>
                                {withdrawal.payoutPreferences?.payoutHandle ||
                                    withdrawal.stripeConnectAccountId ||
                                    'No payout details saved'}
                            </span>
                        </div>
                        <button
                            type="button"
                            className="admin-paid-button"
                            onClick={() => handleMarkPaid(withdrawal.id)}
                            disabled={payingWithdrawalId === withdrawal.id}
                        >
                            {payingWithdrawalId === withdrawal.id ? 'Saving...' : 'Mark paid'}
                        </button>
                    </article>
                ))}
            </section>

            <section className="admin-catalog-warning">
                <FiAlertTriangle />
                <div>
                    <strong>Deploy temporary seed rules first.</strong>
                    <p>Run <code>npm run deploy:rules:seed</code>, seed the catalog, then immediately run <code>npm run deploy:rules</code>.</p>
                </div>
            </section>

            <section className="admin-catalog-panel">
                <label className="admin-file-picker">
                    <FiUploadCloud />
                    <span>Select beat files</span>
                    <input
                        type="file"
                        accept=".mp3,.m4a,.wav,.ogg,.flac,.aac,audio/*"
                        multiple
                        onChange={handleFiles}
                        disabled={isSeeding}
                    />
                </label>

                <div className="admin-catalog-summary">
                    <div>
                        <span>Selected</span>
                        <strong>{files.length}</strong>
                    </div>
                    <div>
                        <span>Ready</span>
                        <strong>{readyFiles.length}</strong>
                    </div>
                    <div>
                        <span>Aliases</span>
                        <strong>{PRODUCERS.length}</strong>
                    </div>
                </div>

                <button
                    type="button"
                    className="admin-seed-button"
                    onClick={seedCatalog}
                    disabled={isSeeding || !readyFiles.length}
                >
                    {isSeeding ? 'Seeding catalog...' : 'Upload & Seed Catalog'}
                </button>
            </section>

            {error && <div className="admin-catalog-error">{error}</div>}

            {plannedFiles.length > 0 && (
                <section className="admin-catalog-table">
                    <h2>File plan</h2>
                    {plannedFiles.map((item) => (
                        <article key={item.file.name} className={`admin-file-row ${item.skipReason ? 'skipped' : ''}`}>
                            <div>
                                <strong>{item.metadata?.title || titleCase(normalize(item.file.name)) || item.file.name}</strong>
                                <span>{item.file.name}</span>
                            </div>
                            <div>{item.producer?.name || 'Unmapped'}</div>
                            <div>{item.skipReason || `${item.metadata.genre} · $${item.metadata.price}`}</div>
                        </article>
                    ))}
                </section>
            )}

            {progress.length > 0 && (
                <section className="admin-catalog-log">
                    <h2>Seed log</h2>
                    {progress.map((item) => (
                        <div key={item.id} className={`admin-log-row ${item.type}`}>
                            <FiCheckCircle />
                            <span>{item.message}</span>
                        </div>
                    ))}
                </section>
            )}
        </main>
    );
};

export default AdminCatalog;
